import { Typography } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { usePoints } from "api/points";
import { DragDropInput } from "components/FormControls/DragDropInput";
import { InfoBox } from "components/Layout/InfoBox";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import { ImportTypes } from "types/import";
import { UnitTransferModal } from "types/unit-transfers";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./ImportUnitsTransfersModal.styles";
import { Modals } from "./helpers";

interface Props {
  type: ImportTypes.UNIT_TRANSFER_ADDING | ImportTypes.UNIT_TRANSFER_SPENDING;
  isModalOpen: boolean;
  closeModal: () => void;
  setModal: (modal: UnitTransferModal) => void;
}

export const ImportUnitsTransfersModal = ({
  type,
  isModalOpen,
  closeModal,
  setModal,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { importUnitsTransfers } = usePoints();
  const navigate = useNavigate();

  const isAddingImportType = type === ImportTypes.UNIT_TRANSFER_ADDING;
  const importTypeLabel = message({
    id: isAddingImportType ? "add" : "deduct",
  }).toLocaleLowerCase();
  const learnMorePath = `https://help.openloyalty.io/main-features/wallets/unit-transfers/creating-unit-transfers#import-${isAddingImportType ? "adding" : "spending"}-units-transfers`;

  return (
    <OLModal
      header={message(
        { id: "importTransfersType" },
        {
          importType: importTypeLabel,
        },
      )}
      onClose={() => closeModal()}
      open={isModalOpen}
      data-test-id="import-units-transfers-modal"
      modalWidth={570}
    >
      <Formik
        initialStatus={{}}
        initialValues={{
          import: undefined,
        }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});

          try {
            const response = await importUnitsTransfers(
              type,
              values.import?.[0],
            );
            if (response) {
              closeModal();
              notify(message({ id: "importHasStartedViewProgress" }), {
                button: {
                  text: message({ id: "view" }),
                  onClick: ({ closeNotification }) => {
                    closeNotification();
                    return navigate(links.importsExports, {
                      state: { activeTab: "imports" },
                    });
                  },
                },
              });
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, status, values }) => {
          const formError = status && (status[""] || status["file"]);
          return (
            <Form onSubmit={handleSubmit}>
              <div className={classes.infoBox}>
                <InfoBox
                  type="warning"
                  text={message(
                    {
                      id: "importUnitsTransfersInfo",
                    },
                    {
                      currentType: importTypeLabel,
                      importType: message({
                        id: isAddingImportType ? "deducting" : "adding",
                      }).toLocaleLowerCase(),
                      clickHere: (
                        <span
                          className={classes.link}
                          onClick={() =>
                            setModal({
                              transfer: "",
                              mode: isAddingImportType
                                ? Modals.ImportSpendingUnitsTransfers
                                : Modals.ImportAddingUnitsTransfers,
                            })
                          }
                        >
                          {message({ id: "clickHere" })}
                        </span>
                      ),
                      learnMore: (
                        <ExternalLink
                          className={classes.link}
                          path={learnMorePath}
                        >
                          {message({ id: "learnMore" })}
                        </ExternalLink>
                      ),
                      span: (chunks: React.ReactNode[]) => (
                        <span className={classes.bolded}>{chunks}</span>
                      ),
                    },
                  )}
                />
              </div>
              {formError && <span className={classes.error}>{formError}</span>}
              <DragDropInput
                data-test-id="import-csv-modal"
                className={classes.dragDropWrapper}
                description={
                  <>
                    <Typography fontSize={16}>
                      <span className={classes.descPurple}>
                        {message({ id: "clickToUpload" })}
                      </span>{" "}
                      {message({ id: "orDragAndDropFileWithTransfers" })}
                    </Typography>
                    <Typography className={classes.supportedFiles}>
                      {message(
                        { id: "jsonXmlFileMaxSize" },
                        {
                          size: 100,
                        },
                      )}
                    </Typography>
                  </>
                }
                header={message(
                  {
                    id: "uploadFileForTransfers",
                  },
                  {
                    currentType: importTypeLabel,
                  },
                )}
                inputClassName={classes.fullWidth}
                singleFile
                onChange={(acceptedFiles, rejectedFiles) => {
                  setFieldValue(
                    "import",
                    acceptedFiles.length > 0
                      ? [acceptedFiles[0]]
                      : [rejectedFiles[0].file],
                  );
                }}
                onDelete={() => setFieldValue("import", undefined)}
                value={values.import}
                error={!!formError}
              />
              <Typography className={classes.linksText}>
                {message(
                  { id: "downloadSampleFiles" },
                  {
                    link: (
                      <ExternalLink path="https://help.openloyalty.io/main-features/imports-exports/imports/sample-import-files">
                        <span className={classes.descPurple}>
                          {message({ id: "here" }).toLocaleLowerCase()}
                        </span>
                      </ExternalLink>
                    ),
                  },
                )}
              </Typography>
              <OLButton
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting || !values.import}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="import-units-transfers-button"
              >
                {message({ id: "importUnitsTransfers" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
